import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Trash2,
  RotateCcw,
  ShieldAlert,
  Package,
  MessageSquare,
  Bell,
  Megaphone,
  Film,
  AlertCircle,
  LifeBuoy,
  FileText,
} from "lucide-react";

const KIND_ICON = {
  product: Package,
  community_post: FileText,
  story: Film,
  dm: MessageSquare,
  staff_msg: MessageSquare,
  ticket: LifeBuoy,
  ticket_msg: LifeBuoy,
  notification: Bell,
  announcement: Megaphone,
  alert: AlertCircle,
};

function timeUntil(iso) {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3600000);
  if (h >= 24) return `${Math.floor(h / 24)}d`;
  if (h >= 1) return `${h}h`;
  return `${Math.max(1, Math.floor(ms / 60000))}m`;
}

function snapshotSummary(row) {
  const s = row.snapshot || {};
  // Best-effort one-line preview based on the kind
  switch (row.kind) {
    case "product":         return s.name || s.title || "(product)";
    case "community_post":  return (s.body || s.title || "").slice(0, 100) || "(post)";
    case "story":           return s.caption || s.media_url || "(story)";
    case "dm":
    case "staff_msg":
    case "ticket_msg":      return (s.body || "").slice(0, 100) || "(message)";
    case "ticket":          return s.subject || s.title || "(ticket)";
    case "notification":    return s.title || s.body || "(notification)";
    case "announcement":    return s.title || s.body || "(announcement)";
    case "alert":           return s.message || s.title || "(alert)";
    default:                return "—";
  }
}

export function TrashTab({ isCEO }) {
  const [data, setData] = useState({ items: [], counts: {}, supported_kinds: [] });
  const [activeKind, setActiveKind] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // trash_id currently in-flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeKind ? { kind: activeKind } : {};
      const { data: d } = await api.get("/admin/trash", { params });
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load trash");
    } finally {
      setLoading(false);
    }
  }, [activeKind]);

  useEffect(() => { load(); }, [load]);

  const restore = async (row) => {
    if (!window.confirm(`Restore this ${row.label.toLowerCase()}?`)) return;
    setBusy(row.trash_id);
    try {
      await api.post(`/admin/trash/${row.trash_id}/restore`);
      toast.success("Restored");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Restore failed");
    } finally {
      setBusy(null);
    }
  };

  const purge = async (row) => {
    if (!window.confirm(`Permanently delete this ${row.label.toLowerCase()}? This cannot be undone.`)) return;
    setBusy(row.trash_id);
    try {
      await api.delete(`/admin/trash/${row.trash_id}`);
      toast.success("Purged");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Purge failed");
    } finally {
      setBusy(null);
    }
  };

  const purgeAllExpired = async () => {
    if (!window.confirm("Permanently delete every item whose 24h window has expired? This cannot be undone.")) return;
    setBusy("__expired");
    try {
      const { data: d } = await api.post("/admin/trash/purge-expired");
      toast.success(`${d.purged} purged`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Purge failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-trash-tab">
      <div className="rounded-2xl p-4 border border-pink-400/25 bg-pink-500/[0.07]" data-testid="trash-header">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-pink-200 font-black inline-flex items-center gap-1.5">
              <Trash2 className="w-3 h-3" /> Trash · 24-hour restore window
            </p>
            <p className="text-[11px] text-zinc-300 mt-1">Soft-deleted items live here until restored or permanently purged.</p>
          </div>
          {isCEO && (
            <Button
              onClick={purgeAllExpired}
              disabled={busy === "__expired"}
              className="rounded-full h-9 px-4 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-400/40 font-bold"
              data-testid="trash-purge-expired-btn"
            >
              <ShieldAlert className="w-3.5 h-3.5 mr-1.5" /> Purge expired
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="trash-kind-tabs">
        <button
          onClick={() => setActiveKind("")}
          className={`shrink-0 rounded-full h-8 px-3 text-xs font-bold uppercase tracking-widest border ${
            !activeKind ? "bg-pink-500/20 text-pink-200 border-pink-400/40" : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
          }`}
          data-testid="trash-kind-all"
        >
          All
        </button>
        {(data.supported_kinds || []).map((k) => {
          const count = data.counts[k.kind] || 0;
          const active = activeKind === k.kind;
          return (
            <button
              key={k.kind}
              onClick={() => setActiveKind(k.kind)}
              className={`shrink-0 rounded-full h-8 px-3 text-xs font-bold uppercase tracking-widest border flex items-center gap-1.5 ${
                active ? "bg-pink-500/20 text-pink-200 border-pink-400/40" : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
              }`}
              data-testid={`trash-kind-${k.kind}`}
            >
              {k.label}
              {count > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-black grid place-items-center">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-center text-zinc-500 py-8">Loading trash…</p>
      ) : data.items.length === 0 ? (
        <p className="text-center text-zinc-500 py-10 text-sm">Trash is empty.</p>
      ) : (
        <div className="space-y-2" data-testid="trash-list">
          {data.items.map((row) => {
            const Icon = KIND_ICON[row.kind] || FileText;
            const tLeft = timeUntil(row.purge_at);
            const expired = tLeft === "expired";
            return (
              <div
                key={row.trash_id}
                className={`rounded-xl p-3 border ${expired ? "border-red-400/40 bg-red-500/[0.06]" : "border-white/10 bg-white/[0.04]"}`}
                data-testid={`trash-row-${row.trash_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/5 grid place-items-center text-zinc-300 shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-black tracking-tight">{row.label}</p>
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/10 text-zinc-300 font-bold">
                        {row.deleted_by_role || "unknown"}
                      </span>
                      <span className={`text-[10px] uppercase tracking-widest font-bold ml-auto ${expired ? "text-red-300" : "text-zinc-400"}`}>
                        {expired ? "Expired" : `Purge in ${tLeft}`}
                      </span>
                    </div>
                    <p className="text-sm mt-0.5 truncate">{snapshotSummary(row)}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      by {row.deleted_by_name || "—"} · {new Date(row.deleted_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      {row.reason ? ` · "${row.reason}"` : ""}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex gap-2 justify-end">
                  <Button
                    onClick={() => restore(row)}
                    disabled={busy === row.trash_id}
                    className="rounded-full h-8 px-3 text-[11px] bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100 border border-emerald-400/30 font-bold"
                    data-testid={`trash-restore-${row.trash_id}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                  {isCEO && (
                    <Button
                      onClick={() => purge(row)}
                      disabled={busy === row.trash_id}
                      className="rounded-full h-8 px-3 text-[11px] bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-400/40 font-bold"
                      data-testid={`trash-purge-${row.trash_id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Purge
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
